import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE, API_HOST } from "config/api";

const toWaPhone = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.startsWith("0") ? "233" + d.slice(1) : d;
};

// Helper function to get image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("blob")) return path;
  return `${API_HOST}/storage/${path}`;
};

// Cell Components
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const MemberInfoCell = ({ value, row }) => (
  <Box display="flex" alignItems="center" gap={2}>
    <Avatar
      src={getImageUrl(row.original.memberPhoto)}
      sx={{ width: 40, height: 40 }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = "";
      }}
    >
      {!row.original.memberPhoto && <PersonIcon fontSize="small" />}
    </Avatar>
    <Box>
      <Typography display="block" variant="button" fontWeight={600}>
        {row.original.memberName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ID: {row.original.memberId}
      </Typography>
    </Box>
  </Box>
);
MemberInfoCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
};

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleNotify }) => (
  <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="success"
      onClick={() => handleNotify(row.original)}
    >
      Notify
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleNotify: PropTypes.func.isRequired,
};

export default function usePaymentData(paymentType = "tithe") {
  const { user, church } = useAuth();
  const searchTimeoutRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const [paymentData, setPaymentData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "Member",
        accessor: "memberName",
        align: "left",
        width: "25%",
        Cell: MemberInfoCell,
      },
      { Header: "Type", accessor: "type", align: "center", width: "10%", Cell: SerialCell },
      {
        Header: "Amount (GHS)",
        accessor: "amount",
        align: "center",
        width: "10%",
        Cell: SerialCell,
      },
      { Header: "Method", accessor: "method", align: "center", width: "10%", Cell: SerialCell },
      {
        Header: "Reference",
        accessor: "reference",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Payment Date",
        accessor: "paymentDate",
        align: "center",
        width: "12%",
        Cell: SerialCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "8%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "15%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleNotify={handleNotify}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Check for payment success on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentRef = params.get("reference");
    const paymentStatus = params.get("payment");

    if (paymentStatus === "success" && paymentRef) {
      verifyPayment(paymentRef);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const verifyPayment = async (reference) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/payments/verify?reference=${reference}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.status) {
        Swal.fire({
          title: "Payment Successful!",
          text: `Thank you for your payment of GHS ${data.payment.amount}`,
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchPayments();
      } else {
        Swal.fire({
          title: "Verification Failed",
          text: data.message || "Could not verify payment",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred while verifying payment",
        icon: "error",
      });
    }
  };

  const searchMembers = useCallback(
    async (query) => {
      if (!church?.id || !query.trim()) {
        setFilteredMembers([]);
        return;
      }

      setIsLoadingMembers(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const response = await fetch(
          `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        setFilteredMembers(result.data || []);
      } catch (err) {
        console.error("Search failed:", err);
        setFilteredMembers([]);
      } finally {
        setIsLoadingMembers(false);
      }
    },
    [church?.id]
  );

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }

    const timer = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchMembers]);

  const loadAllMembers = useCallback(async () => {
    if (!church?.id) return;

    setIsLoadingMembers(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/members/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");
      const data = await response.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  const handleMemberSelect = (member) => {
    setSelectedPayment((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name} ${member.surname}`,
      memberId: member.memberID,
      memberPhoto: member.photo,
      member: member,
    }));
    setSearchTerm(`${member.first_name} ${member.surname} (${member.memberID})`);
    setShowMemberDropdown(false);
  };

  const handleEdit = useCallback((payment) => {
    setSelectedPayment({
      ...payment,
      paymentDate: payment.paymentDate || new Date().toISOString().split("T")[0],
    });
    setSearchTerm(payment.memberName || "");
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((payment) => {
    setSelectedPayment(payment);
    setDeleteModalOpen(true);
  }, []);

  const handleNotify = useCallback((payment) => {
    const name = payment.memberName || "Member";
    const phone = payment.phone;
    const email = payment.email;
    const msg = `Dear ${name}, your payment of ${payment.amount} has been recorded on ${payment.paymentDate}. Thank you!`;
    Swal.fire({
      title: `Notify ${name}`,
      html: `
        <p style="margin:4px 0 16px;color:#555;font-size:13px;text-align:left">"${msg}"</p>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap">
          <button type="button" id="sn-sms" style="background:#1976d2;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">📱 SMS</button>
          <button type="button" id="sn-wa" style="background:#25D366;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">💬 WhatsApp</button>
          <button type="button" id="sn-email" style="background:#0288d1;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">✉️ Email</button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      didOpen: (popup) => {
        popup.querySelector("#sn-sms")?.addEventListener("click", () => {
          Swal.close();
          if (!phone) {
            Swal.fire("No phone", `${name} has no phone number on file.`, "warning");
            return;
          }
          const token = localStorage.getItem("authToken");
          fetch(`${API_BASE}/v3/sms/send`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ recipient: phone, message: msg, type: "payment_receipt" }),
          })
            .then(() =>
              Swal.fire({
                title: "Sent!",
                text: `SMS sent to ${name}.`,
                icon: "success",
                timer: 2000,
              })
            )
            .catch(() => Swal.fire("Error", "Failed to send SMS.", "error"));
        });
        popup.querySelector("#sn-wa")?.addEventListener("click", () => {
          Swal.close();
          const cleaned = toWaPhone(phone);
          if (!cleaned) {
            Swal.fire("No phone", `${name} has no phone number on file.`, "warning");
            return;
          }
          window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(msg)}`, "_blank");
        });
        popup.querySelector("#sn-email")?.addEventListener("click", () => {
          Swal.close();
          if (!email) {
            Swal.fire("No email", `${name} has no email address on file.`, "warning");
            return;
          }
          window.open(
            `mailto:${email}?subject=Payment%20Receipt&body=${encodeURIComponent(msg)}`,
            "_blank"
          );
        });
      },
    });
  }, []);

  const handleEditSubmit = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedPayment.id
        ? `${API_BASE}/payments/${selectedPayment.id}`
        : `${API_BASE}/payments`;
      const method = selectedPayment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: selectedPayment.member_id,
          type: paymentType,
          amount: selectedPayment.amount,
          method: selectedPayment.method || "manual",
          reference: selectedPayment.reference || "",
          payment_date: selectedPayment.paymentDate,
          status: selectedPayment.status,
          church_id: church.id,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: `Payment successfully ${selectedPayment.id ? "updated" : "created"}`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setEditModalOpen(false);
      setSearchTerm("");
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment, church?.id, paymentType]);

  const handlePaystackSubmit = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Please login to continue");

      const response = await fetch(`${API_BASE}/payments/paystack/initialize`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: selectedPayment.member_id,
          email: selectedPayment.email,
          amount: selectedPayment.amount,
          type: paymentType,
          church_id: church.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Payment initialization failed");
      }

      if (data.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
      } else {
        throw new Error("No payment URL received");
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedPayment, church?.id, paymentType]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments/${selectedPayment.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "Payment deleted successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment]);

  const handleAddPayment = useCallback(() => {
    const defaultAmount =
      paymentType === "welfare" && church?.welfare_amount ? String(church.welfare_amount) : "";
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const dayStr = String(now.getDate()).padStart(2, "0");
    const monthStr = String(currentMonth).padStart(2, "0");
    const paymentDate = `${currentYear}-${monthStr}-${dayStr}`;
    setSelectedPayment({
      id: null,
      member_id: "",
      member_name: "",
      memberId: "",
      memberPhoto: "",
      type: paymentType,
      amount: defaultAmount,
      method: "manual",
      reference: `${paymentType.toUpperCase()}-${Date.now().toString().slice(-6)}`,
      paymentDate,
      period_month: currentMonth,
      period_year: currentYear,
      status: "pending",
    });
    setSearchTerm("");
    setFilteredMembers([]);
    setEditModalOpen(true);
  }, [paymentType, church?.welfare_amount]);

  const handlePrint = useCallback(() => window.print(), []);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        `${API_BASE}/payments/members/${church.id}?type=${paymentType}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const payments = result.data || result.payments || result;

      // Sort payments in descending order (latest first)
      const sortedPayments = [...payments].sort((a, b) => {
        return new Date(b.payment_date) - new Date(a.payment_date);
      });

      setPaymentData((prev) => ({
        ...prev,
        rows: sortedPayments.map((pay, index) => ({
          id: pay.id,
          serial: index + 1,
          memberId: pay.member?.memberID || "N/A",
          memberName: `${pay.member?.first_name || ""} ${pay.member?.surname || ""}`,
          memberPhoto: pay.member?.photo || "",
          phone: pay.member?.phone || "",
          email: pay.member?.email || "",
          type: pay.type,
          amount: `GHS ${parseFloat(pay.amount).toLocaleString()}`,
          method: pay.method,
          reference: pay.reference,
          paymentDate: new Date(pay.payment_date).toLocaleDateString(),
          status: pay.status || "pending",
          createdAt: pay.created_at,
          originalData: pay,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setPaymentData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church, paymentType]);

  useEffect(() => {
    if (church?.id) {
      fetchPayments();
      loadAllMembers();
    }
  }, [church?.id, fetchPayments, loadAllMembers]);

  return {
    ...paymentData,
    members,
    isLoadingMembers,
    filteredMembers,
    handleMemberSelect,
    handleEdit,
    handleDelete,
    handleNotify,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handlePaystackSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    searchTerm,
    setSearchTerm,
    loadAllMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    searchMembers,
  };
}
